/**
 * Every payment Naurist has taken, or tried to.
 *
 * One endpoint, paged and filtered on the server — the tab, the date range and
 * the page all travel as query params, so the browser never holds the whole
 * ledger to narrow it. The counts the tab bar could wear come back with every
 * page under `tab_counts`, describing the range rather than the page, so they
 * do not move when a tab is switched.
 *
 * Money arrives as a decimal string — `"800.00"` — because the server keeps it
 * exact and JSON has no decimal type. It is parsed to a number here and
 * written by `formatRupees` at the edge.
 */

import { API_ENDPOINTS, api } from "../../../lib/api";
import { formatDate, type UserPlan } from "../../users";
import type {
  Payment,
  PaymentStatus,
  PaymentTabCounts,
  PaymentTabId,
  PaymentsPage,
} from "../types/revenue.types";
import { dateRangeFor, type DateRangeId } from "../dateRanges";

export interface PaymentsQuery {
  /** 1-based, the way the pagination control counts. */
  page: number;
  pageSize: number;
  tab: PaymentTabId;
  range: DateRangeId;
}

interface PaymentResponse {
  /** Which account was billed. The list carries no id of its own. */
  workspace_id: number;
  account_name?: string | null;
  /** Already one line — "Mumbai, Maharashtra". The Place column. */
  location?: string | null;
  /** Already the panel's own words — "Monthly", "Yearly". */
  plan?: string | null;
  amount?: string | number | null;
  /**
   * Always "INR" so far, and deliberately not read: the panel writes rupees,
   * and the day a second currency turns up it needs a column of its own rather
   * than a symbol that quietly changes under the same figures.
   */
  currency?: string | null;
  /** ISO 8601, with an offset. */
  date?: string | null;
  /** Title case, unlike the tab names — "Pending authorization". */
  status?: string | null;
}

interface PaymentsListResponse {
  count?: number | null;
  next?: string | null;
  previous?: string | null;
  results?: PaymentResponse[] | null;
  tab_counts?: Partial<Record<PaymentTabId, number | null>> | null;
}

/** Money, out of the decimal string the server sends it as. */
const toAmount = (value: string | number | null | undefined): number => {
  const amount = typeof value === "string" ? Number(value) : value;
  return typeof amount === "number" && Number.isFinite(amount) ? amount : 0;
};

/** A plan the panel has not been taught about falls back rather than throwing —
 *  one unfamiliar word should not blank the whole list. */
const toPlan = (value: string | null | undefined): UserPlan =>
  value === "Monthly" || value === "Yearly" ? value : "Free trial";

const PAYMENT_STATUSES: PaymentStatus[] = [
  "Active",
  "Completed",
  "Pending authorization",
  "Abandoned",
  "Expired",
];

/**
 * The API's word for where a payment got to, in the panel's — which are the
 * same words, so this is really a check that it is one of the five.
 *
 * An unrecognised one reads as `Pending authorization`: of the five it is the
 * only one that claims nothing has happened yet, which is the safest thing to
 * say about a state the panel does not understand.
 *
 * Exported because the dashboard's activity feed maps the same field out of a
 * different endpoint, and one vocabulary read two ways is two vocabularies.
 */
export const toPaymentStatus = (
  value: string | null | undefined
): PaymentStatus =>
  PAYMENT_STATUSES.find((status) => status === value) ??
  "Pending authorization";

const toCounts = (
  counts: PaymentsListResponse["tab_counts"]
): PaymentTabCounts => {
  const read = (key: PaymentTabId) => counts?.[key] ?? undefined;

  return {
    all: read("all"),
    active: read("active"),
    abandoned: read("abandoned"),
    expired: read("expired"),
    pending_authorization: read("pending_authorization"),
  };
};

const toPayment = (row: PaymentResponse): Payment => ({
  // The account and the moment together — see the note on `Payment.id`.
  id: `${row.workspace_id}-${row.date ?? ""}`,
  workspaceId: row.workspace_id,
  accountName: row.account_name?.trim() || "Unnamed account",
  place: row.location?.trim() || "—",
  plan: toPlan(row.plan),
  amount: toAmount(row.amount),
  date: formatDate(row.date),
  status: toPaymentStatus(row.status),
});

/** One page of the payments list, filtered and counted by the server. */
export const listPayments = async ({
  page,
  pageSize,
  tab,
  range,
}: PaymentsQuery): Promise<PaymentsPage> => {
  const { from, to } = dateRangeFor(range);

  const data = await api.get<PaymentsListResponse>(API_ENDPOINTS.payments.list, {
    query: {
      // The All tab is the absence of a status filter rather than a status the
      // server knows, so it is left off entirely. Same for a range of "all
      // time", which `dateRangeFor` answers with no days for.
      status: tab === "all" ? undefined : tab,
      from,
      to,
      page,
      page_size: pageSize,
    },
  });

  return {
    payments: (data.results ?? []).map(toPayment),
    total: data.count ?? 0,
    tabCounts: toCounts(data.tab_counts),
  };
};
